import React, { useMemo } from 'react';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
  ComposedChart,
} from 'recharts';
import { Box, Typography, useTheme } from '@mui/material';
import { ServiceOpportunity, ServiceDefinition, AstronomicalData } from '../../models';
import { parseTimeString } from '../../utils/dateUtils';

interface TideChartProps {
  tideData: {
    time: string;
    height: number;
  }[];
  serviceOpportunities: ServiceOpportunity[];
  date: string;
  services?: ServiceDefinition[]; // All service definitions
  astronomicalData?: AstronomicalData; // Optional astronomical data
  monthlyTideStatistics?: { minHeight: number; maxHeight: number }; // Month-wide tide statistics
}

/**
 * Component for visualizing tide data and service opportunities
 */
const TideChart: React.FC<TideChartProps> = ({
  tideData,
  serviceOpportunities,
  date,
  services = [], // Default to empty array
  astronomicalData,
  monthlyTideStatistics
}) => {
  const theme = useTheme();

  // If we don't have astronomical data, just use undefined
  const astroData = astronomicalData || undefined;

  if (!astroData) {
    console.warn('TideChart: No astronomical data provided');
  }

  // Extract time values for astronomical events (minutes since midnight)
  const getTimeValue = (timeString?: string): number | null => {
    if (!timeString) return null;
    return parseTimeString(timeString);
  };

  // Format data for the tide chart with proper time objects for accurate scaling
  const chartData = tideData.map((item) => {

    const timeValue = getTimeValue(item.time); // Time in minutes since midnight

    return {
      time: item.time, // Formatted time for display
      height: item.height,
      timeISO: item.time, // Keep original ISO time string
      timeValue: timeValue // Numerical value for scaling (minutes since midnight)
    };
  });

  // Get astronomical time values
  const sunriseTime = astroData?.sunriseTime ? getTimeValue(astroData.sunriseTime) : null;
  const sunsetTime = astroData?.sunsetTime ? getTimeValue(astroData.sunsetTime) : null;
  const moonriseTime = astroData?.moonriseTime ? getTimeValue(astroData.moonriseTime) : null;
  const moonsetTime = astroData?.moonsetTime ? getTimeValue(astroData.moonsetTime) : null;

  // Get the minimum and maximum heights for y-axis
  // For frontend presentation - always use 0 as minimum for better visualization
  const minHeight = 0;
  
  // Use monthlyTideStatistics if available for consistent y-axis across the month
  // Otherwise fall back to calculating from the current day's data
  const maxHeight = monthlyTideStatistics
    ? Math.ceil(monthlyTideStatistics.maxHeight + 0.5)
    : Math.ceil(Math.max(...tideData.map((item) => item.height)) + 0.5);

  // We're using ReferenceLines instead of scatter points for astronomical events

  // Custom tooltip for the tide chart
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      // Format the label (which is timeValue) back to HH:mm
      const hours = Math.floor(label / 60);
      const minutes = label % 60;
      const formattedLabel = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      return (
        <Box
          sx={{
            backgroundColor: 'background.paper',
            p: 1,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 1,
            maxWidth: 250
          }}
        >
          <Typography variant="subtitle2">{formattedLabel}</Typography>
          <Typography variant="body2" color="primary">
            Tide Height: {payload[0].value.toFixed(2)}m
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // Extract all unique tide constraints from service definitions
  const uniqueTideConstraints = useMemo(() => {
    const constraints = new Map<number, {label: string, serviceIds: string[], type: 'launch' | 'retrieval'}>();

    services.forEach(service => {
      // Add launch height constraint if required
      if (service.tideRestrictions.launchRequired) {
        const height = service.tideRestrictions.launchMinHeight;
        const key = height;

        if (!constraints.has(key)) {
          constraints.set(key, {
            label: `Launch Min Height`,
            serviceIds: [],
            type: 'launch'
          });
        }

        // Add service ID to this constraint
        constraints.get(key)!.serviceIds.push(service.id);
      }

      // Add retrieval height constraint if required
      if (service.tideRestrictions.retrievalRequired) {
        const height = service.tideRestrictions.retrievalMinHeight;
        const key = height;

        if (!constraints.has(key)) {
          constraints.set(key, {
            label: `Retrieval Min Height`,
            serviceIds: [],
            type: 'retrieval'
          });
        }

        // Add service ID to this constraint
        constraints.get(key)!.serviceIds.push(service.id);
      }
    });

    return constraints;
  }, [services]);

  // Get service opportunity areas for the chart
  const serviceAreas = serviceOpportunities.map((opportunity) => {
    const startTimeValue = new Date(opportunity.startTime).getHours() * 60 + new Date(opportunity.startTime).getMinutes();
    const endTimeValue = new Date(opportunity.endTime).getHours() * 60 + new Date(opportunity.endTime).getMinutes();

    // Find the indices in chartData
    const startIndex = chartData.findIndex((item) => item.timeValue === startTimeValue);
    const endIndex = chartData.findIndex((item) => item.timeValue === endTimeValue);

    return {
      startTimeValue,
      endTimeValue,
      startIndex: startIndex >= 0 ? startIndex : 0,
      endIndex: endIndex >= 0 ? endIndex : chartData.length - 1,
      serviceId: opportunity.serviceId,
      // Look up service name from the services prop
      serviceName: services.find(s => s.id === opportunity.serviceId)?.name ?? 'Unknown',
      launchHeight: opportunity.tideHeightAtLaunch,
      retrievalHeight: opportunity.tideHeightAtRetrieval,
      launchViable: opportunity.launchViable,
      retrievalViable: opportunity.retrievalViable,
    };
  });

  // Helper function to format minutes since midnight to HH:mm
  const formatMinutesToTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  return (
    <Box sx={{ width: '100%', mb: 4 }}>
      {/* Main tide chart */}
      <Box sx={{ width: '100%', height: 500 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={chartData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 50,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" verticalPoints={Array.from({length: 13}, (_, i) => i * 120)} />
          <XAxis
            type="number"
            dataKey="timeValue"
            domain={[0, 24*60]} // Set domain to cover 0 to 24 hours in minutes
            ticks={Array.from({length: 13}, (_, i) => i * 120)}
            tickFormatter={(value) => {
              const hours = Math.floor(value / 60);
              const minutes = value % 60;
              return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
            }}
            label={{ value: 'Time', position: 'insideBottom', offset: -25, fontSize: 11, fill: theme.palette.text.secondary }}
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }} // Also style the tick labels
          />
          <YAxis
            domain={[minHeight, maxHeight]}
            label={{ value: 'Tide Height (m)', angle: -90, position: 'insideLeft', fontSize: 11, fill: theme.palette.text.secondary }}
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }} // Also style the tick labels
          />
          <RechartsTooltip content={<CustomTooltip />} />
          {/* Main tide height line */}
          <Line
            type="monotone"
            dataKey="height"
            stroke={theme.palette.primary.main}
            strokeWidth={3}
            dot={false}
            activeDot={{ r: 8 }}
            name="Tide Height"
          />

          {/* Add service opportunity areas */}
          {serviceAreas.map((area, index) => {
            // Get color from service definition - with fallback for resilience
            const serviceDef = services.find(s => s.id === area.serviceId);
            let serviceColor = '#808080'; // Default gray fallback color

            if (serviceDef) {
              serviceColor = serviceDef.color;
            } else {
              console.warn(`Service definition not found for ID: ${area.serviceId}, using fallback color`);
            }

            return (
              <ReferenceArea
                key={`service-area-${index}`}
                x1={area.startTimeValue}
                x2={area.endTimeValue}
                stroke={serviceColor}
                strokeOpacity={0.5}
                fill={serviceColor}
                fillOpacity={0.15}
                label={{
                  value: `${area.serviceName}\n${formatMinutesToTime(area.startTimeValue)} - ${formatMinutesToTime(area.endTimeValue)}`, // Corrected label value
                  position: 'center',
                  angle: 270,
                  fill: serviceColor,
                  fontSize: 14,
                  fontWeight: 'bold',
                }}
              />
            );
          })}

          {/* Reference lines for all unique tide constraints */}
          {Array.from(uniqueTideConstraints.entries()).map(([height, constraint]) => {
            return (
              <ReferenceLine
                key={`constraint-${height}-${constraint.type}`}
                y={height}
                stroke="#ff0000" // Red line
                strokeDasharray="3 3"
                strokeWidth={1}
                ifOverflow="extendDomain"
                label={{
                  value: `${height.toFixed(2)}m`,
                  position: 'left',
                  fill: "#ff0000", // Red text
                  fontSize: 12 // Match standard y-axis label size
                }}
              />
            );
          })}
          {/* Add vertical reference lines for celestial events */}
          {sunriseTime !== null && (
            <ReferenceLine
              x={sunriseTime}
              stroke={theme.palette.warning.main}
              strokeWidth={3} // Increased width
              label={{
                value: `Sunrise (${astroData?.sunriseTime})`,
                position: 'top',
                fill: theme.palette.warning.main,
                fontSize: 11, // Updated font size
                fontWeight: 'bold'
              }}
            />
          )}

          {sunsetTime !== null && (
            <ReferenceLine
              x={sunsetTime}
              stroke={theme.palette.warning.dark}
              strokeWidth={3} // Increased width
              label={{
                value: `Sunset (${astroData?.sunsetTime})`,
                position: 'top',
                fill: theme.palette.warning.dark,
                fontSize: 11, // Updated font size
                fontWeight: 'bold'
              }}
            />
          )}

          {moonriseTime !== null && (
            <ReferenceLine
              x={moonriseTime}
              stroke={theme.palette.info.light}
              strokeWidth={3} // Increased width
              label={{
                value: `Moonrise (${astroData?.moonriseTime})`,
                position: 'bottom',
                fill: theme.palette.info.light,
                fontSize: 11, // Updated font size
                fontWeight: 'bold',
                dy: 15 // Added vertical offset
              }}
            />
          )}

          {moonsetTime !== null && (
            <ReferenceLine
              x={moonsetTime}
              stroke={theme.palette.info.dark}
              strokeWidth={3} // Increased width
              label={{
                value: `Moonset (${astroData?.moonsetTime})`,
                position: 'bottom',
                fill: theme.palette.info.dark,
                fontSize: 11, // Updated font size
                fontWeight: 'bold',
                dy: 15 // Added vertical offset
              }}
            />
          )}

          {/* Add vertical reference lines for launch and retrieval times */}
          {serviceAreas.map((area, index) => {
            return (
              <React.Fragment key={`service-refs-${index}`}>
                {/* Vertical lines at start and end times */}
                {area.launchViable && (
                  <ReferenceLine
                    x={area.startTimeValue}
                    stroke={theme.palette.success.main}
                    strokeDasharray="8 4" // Keep this dashed or make solid?
                    label={{
                      value: `Launch: ${area.launchHeight.toFixed(2)}m`,
                      position: 'bottom',
                      fill: theme.palette.success.main,
                      fontSize: 12,
                    }}
                  />
                )}
                {area.retrievalViable && (
                  <ReferenceLine
                    x={area.endTimeValue}
                    stroke={theme.palette.info.main}
                    strokeDasharray="8 4" // Keep this dashed or make solid?
                    label={{
                      value: `Retrieval: ${area.retrievalHeight.toFixed(2)}m`,
                      position: 'bottom',
                      fill: theme.palette.info.main,
                      fontSize: 12,
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </ComposedChart>
      </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default TideChart;
